import { computed, inject, Injectable, signal } from '@angular/core';
import { EMPTY, fromEvent, interval } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { PlanningHasEntity } from '../classes/planning-has-entity.class';
import { UserPlanning } from '../classes/user-planning';
import { ApiResponse, ApiService } from './api/api.service';
import { AuthorisationService } from './auth/authorisation.service';
import { Functionalities } from './auth/functionalities';

const SEVERITY_POLL_INTERVAL_MS = 60000;

export interface PlanningDetailsOptions {
    user_id?: number;
    entity_id?: number;
    project_id?: string;
    from?: string;
    to?: string;
    recently_updated?: boolean;
}

interface PlanningDetailsResponse {
    data: {
        planning_has: PlanningHasEntity[];
        user_planning: UserPlanning[];
    };
}

export type CodaltSeverity = 'error' | 'warning' | null;

interface TodaySeverityResponse {
    logs: CodaltSeverity;
    failedJobs: CodaltSeverity;
    appLogs: CodaltSeverity;
}

@Injectable({
    providedIn: 'root',
})
export class CodaltModusService {
    readonly enabled = signal(AuthorisationService.hasFunctionality(Functionalities.CodaltMode));

    logsSeverity = signal<CodaltSeverity>(null);
    failedJobsSeverity = signal<CodaltSeverity>(null);
    appLogsSeverity = signal<CodaltSeverity>(null);

    overallSeverity = computed<CodaltSeverity>(() => {
        const severities = [this.logsSeverity(), this.failedJobsSeverity(), this.appLogsSeverity()];
        if (severities.includes('error')) return 'error';
        if (severities.includes('warning')) return 'warning';
        return null;
    });

    private apiService = inject(ApiService);

    constructor() {
        if (AuthorisationService.hasFunctionality(Functionalities.CodaltMode)) {
            fromEvent(document, 'visibilitychange')
                .pipe(
                    startWith(null),
                    map(() => document.visibilityState === 'visible'),
                    switchMap(isVisible => (isVisible ? interval(SEVERITY_POLL_INTERVAL_MS).pipe(startWith(0)) : EMPTY))
                )
                .subscribe(() => this.refreshTodaySeverity());
        }
    }

    toggle() {
        this.enabled.update(v => !v);
    }

    refreshTodaySeverity() {
        if (!this.enabled() || !AuthorisationService.hasFunctionality(Functionalities.CodaltMode)) {
            this.logsSeverity.set(null);
            this.failedJobsSeverity.set(null);
            this.appLogsSeverity.set(null);
            return;
        }

        this.apiService.getCallObs<ApiResponse<TodaySeverityResponse>>('codalt/today-severity').subscribe(response => {
            this.logsSeverity.set(response.data.logs);
            this.failedJobsSeverity.set(response.data.failedJobs);
            this.appLogsSeverity.set(response.data.appLogs);
        });
    }

    getPlanningDetails(options?: PlanningDetailsOptions) {
        const params = this.buildParams(options);
        return this.apiService.getCallObs<PlanningDetailsResponse>('codalt/planning-details', params).pipe(
            map(response => {
                const planning_has = response.data.planning_has;
                const user_planning = response.data.user_planning;
                return [...planning_has, ...user_planning].sort(
                    (a, b) => new Date(b.begindate).getTime() - new Date(a.begindate).getTime()
                );
            })
        );
    }

    private buildParams(params: any) {
        // loop through the params and remove empty properties
        const result: any = {};
        for (const key in params) {
            if (params[key] !== null && params[key] !== undefined) {
                result[key] = params[key];
            }
        }
        return result;
    }
}
